import React, { ReactElement, useState } from 'react';
import moment from 'moment';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import queryString from 'query-string';
import { ApolloError } from '@apollo/client';
import { ModalActionsPanel } from '@odin-labs/components';
import {
  JobsiteWorkerOrientationStatus,
  UserErrorCode,
  ContractorWorker,
  useGetAddJobsiteContractorWorkerJobsiteQuery,
  useAssignContractorWorkerToJobsiteMutation,
  useUpdateJobsiteWorkerMutation,
  JobsiteWorker,
  useCreateContractorWorkerMutation,
  CreateContractorWorkerMutationVariables,
} from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import { LoadingError } from 'components/loadingError';
import { Header } from 'components/header';
import { Container } from 'components/container';
import {
  getGraphQLError,
  getGraphQLErrorByCode,
  getGraphQLErrorByName,
} from 'utils/error';
import {
  Form,
  FormFieldsConfig,
  FormOnSubmit,
  HTMLFormElementWithApi,
} from 'components/form';
import { FullLayoutNavbarContainer } from 'containers/fullLayoutNavbar';
import {
  momentISODateFormatter,
  getCurrentISOFormattedDate,
  getPhoneNumberAsE164,
} from 'utils';
import { WorkersFound } from 'components/modals/types';
import { WorkersFoundModal } from 'components/modals/WorkersFoundModal';
import { getCurrentISOFormattedDateTime, momentFormatter } from 'utils/dates';
import { AlreadyExistingItem } from 'components/alreadyExistingItem';
import {
  EmailAlreadyExistsErrorExtensions,
  PhoneAlreadyExistsErrorExtensions,
} from 'types';
import {
  getFormInputs,
  getDefaultValues,
  configFieldMap,
} from './AddJobsiteContractorWorkerContainer.forms';
import { BasicInfoFormData } from './types';
import { OnboardingStepKey, getStepFieldsConfig } from './helpers/utils';

export function AddJobsiteContractorWorkerContainer(): ReactElement {
  const history = useHistory();
  const location = useLocation();
  const { contractorId, jobsiteId } = useParams<{
    contractorId: string;
    jobsiteId: string;
  }>();
  const params = queryString.parse(location.search);
  const [fetching, setFetching] = useState<boolean>(false);
  const [workerMatch, setWorkerMatch] = useState<WorkersFound>(null);
  const [basicInfo, setBasicInfo] = useState<BasicInfoFormData>();

  const { orientationDate, workType, tenant } = params;

  const [existingWorkers, setExistingWorkers] = useState<WorkersFound[]>([]);

  const {
    data: jobsiteData,
    error: jobsiteError,
    loading: jobsiteLoading,
  } = useGetAddJobsiteContractorWorkerJobsiteQuery({
    fetchPolicy: 'no-cache',
    variables: { jobsiteId },
    skip: !jobsiteId,
  });

  const jobsite = jobsiteData?.getJobsite;

  const [updateJobsiteWorker] = useUpdateJobsiteWorkerMutation({
    onError: (error) => {
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    },
    refetchQueries: ['GetJobsiteWorker'],
  });

  const [assignContractorWorkerToJobsite] =
    useAssignContractorWorkerToJobsiteMutation({
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        setFetching(false);
        const { orientationStatus, jobsiteWorkerId, contractorWorker } =
          data?.assignContractorWorkerToJobsite.jobsiteWorker ?? {};

        if (
          moment(orientationDate).format(momentISODateFormatter) ===
          getCurrentISOFormattedDate()
        ) {
          if (orientationStatus === JobsiteWorkerOrientationStatus.NotStarted) {
            updateJobsiteWorker({
              variables: {
                jobsiteWorkerId,
                jobsiteWorkerInput: {
                  orientationStatus: JobsiteWorkerOrientationStatus.InProgress,
                  orientationDate: getCurrentISOFormattedDateTime(),
                  isAntipassbackExempt: false,
                  workType: workType as string,
                  fitoutTenant: tenant as string,
                },
              },
            });
          }
          history.push(`/onboarding/${jobsiteWorkerId}/personal-information`);
        } else {
          updateJobsiteWorker({
            variables: {
              jobsiteWorkerId,
              jobsiteWorkerInput: {
                orientationStatus: JobsiteWorkerOrientationStatus.NotStarted,
                isAntipassbackExempt: false,
                workType: workType as string,
                fitoutTenant: tenant as string,
              },
            },
          });
          history.push(`/worker/${contractorWorker?.worker?.workerId}`);
        }
      },
      onError: (responseError: ApolloError) => {
        setFetching(false);
        AlertService.alert(
          'danger',
          'Something went wrong!',
          getGraphQLError(responseError),
        );
      },
    });

  const formRef = React.useRef<HTMLFormElementWithApi<BasicInfoFormData>>();

  // creates a new contactor worker
  const [createContractorWorker] = useCreateContractorWorkerMutation({
    fetchPolicy: 'no-cache',
    onCompleted: async (data) => {
      setWorkerMatch(null);
      assignContractorWorkerToJobsite({
        variables: {
          input: {
            workerId: data?.createContractorWorker?.worker?.workerId,
            jobsiteId,
            contractorId,
            jobsiteWorkerInput: {
              startDate: getCurrentISOFormattedDate(),
              orientationDate: orientationDate
                ? moment(orientationDate).format(momentISODateFormatter)
                : null,
            },
          },
        },
      });
    },
    onError: (error: ApolloError) => {
      setFetching(false);
      const emailAlreadyExistsError =
        getGraphQLErrorByCode<EmailAlreadyExistsErrorExtensions>(
          error,
          UserErrorCode.EmailAlreadyExists,
        );
      if (emailAlreadyExistsError) {
        formRef.current.api.setError('email', {
          message: (
            <AlreadyExistingItem
              itemType="Email"
              workerId={emailAlreadyExistsError.extensions.workerId}
            />
          ),
          shouldFocus: true,
        });
        return;
      }
      const phoneAlreadyExistsError =
        getGraphQLErrorByCode<PhoneAlreadyExistsErrorExtensions>(
          error,
          UserErrorCode.PhoneAlreadyExists,
        );
      if (phoneAlreadyExistsError) {
        formRef.current.api.setError('phoneNumber', {
          message: (
            <AlreadyExistingItem
              itemType="Phone"
              workerId={phoneAlreadyExistsError.extensions.workerId}
            />
          ),
          shouldFocus: true,
        });
        return;
      }

      const workerAlreadyAssignedToContractorError = getGraphQLErrorByName<{
        contractorWorker: ContractorWorker;
        jobsiteWorkers: JobsiteWorker[];
      }>(error, 'WorkerAlreadyAssignedToContractorError');
      if (workerAlreadyAssignedToContractorError) {
        const { contractorWorker, jobsiteWorkers } =
          workerAlreadyAssignedToContractorError.extensions || {};
        const { worker } = contractorWorker || {};
        const currentWorkerMatch: WorkersFound = {
          firstName: worker?.user?.identity?.firstName,
          lastName: worker?.user?.identity?.lastName,
          jobsiteWorkerInfo: jobsiteWorkers?.map(
            ({ jobsiteWorkerId, trade, jobTitle }) => ({
              jobsiteWorkerId,
              trade,
              jobTitle,
            }),
          ),
          workerId: worker?.workerId,
        };
        setExistingWorkers([currentWorkerMatch]);
        return;
      }

      const workerAlreadyExistsError = getGraphQLErrorByName<{
        workerInfo: WorkersFound[];
      }>(error, 'WorkerAlreadyExistsError');
      if (workerAlreadyExistsError) {
        const users = workerAlreadyExistsError.extensions?.workerInfo;
        setExistingWorkers(users);
        return;
      }
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    },
  });

  const getCreateContractorWorkerVariables = (
    data: BasicInfoFormData,
    forceCreate: boolean,
  ): CreateContractorWorkerMutationVariables => ({
    createContractorWorkerInput: {
      forceCreate,
      firstName: data.firstName?.trim(),
      lastName: data.lastName?.trim(),
      birthDate:
        data.birthDate &&
        moment
          .utc(data.birthDate, momentFormatter)
          .format(momentISODateFormatter),
      ssnLastFour: data.ssnLastFour?.trim() || null,
      contractorId,
      phoneNumber: getPhoneNumberAsE164(data.phoneNumber) || null,
      email: data.email || null,
      middleInitial: data.middleInitial || null,
      suffix: data.suffix || null,
      contractorWorkerInput: {
        startDate: getCurrentISOFormattedDate(),
      },
    },
  });

  const onSubmit: FormOnSubmit<BasicInfoFormData> = (data): void => {
    if (fetching) {
      return;
    }

    setFetching(true);
    setBasicInfo(data);

    createContractorWorker({
      variables: getCreateContractorWorkerVariables(data, false),
    });
  };

  const formInputs = React.useMemo(() => getFormInputs(), []);
  const defaultValues = React.useMemo(
    () => getDefaultValues(basicInfo),
    [basicInfo],
  );
  const fieldsConfig = React.useMemo(
    (): FormFieldsConfig => ({
      fields: getStepFieldsConfig(OnboardingStepKey.PersonalInfo, jobsite),
      inputToFieldMap: configFieldMap,
    }),
    [jobsite],
  );

  if (jobsiteError) {
    return <LoadingError error={jobsiteError} />;
  }

  const loading = fetching || jobsiteLoading;

  return (
    <Container className="add-jobsite-contractor-worker-container lg:odin-max-w-5xl lg:odin-mx-auto">
      <FullLayoutNavbarContainer />
      <Header
        title="Let's start with the basics."
        subtitle="Please enter the following information about the worker."
      />
      <div className="odin-w-full odin-relative">
        {loading && (
          <div className="table-backdrop odin-m--5">
            <LoadingError loading />
          </div>
        )}
        <Form
          ref={formRef}
          inputs={formInputs}
          defaultValues={defaultValues}
          onSubmit={onSubmit}
          renderBelow={(): ReactElement => (
            <ModalActionsPanel
              actionButtonType="submit"
              actionButtonWithSpinner={fetching}
              onCancel={(): void => {
                history.goBack();
              }}
            />
          )}
          className="odin-p-3"
          inputsContainerClassName="odin-grid odin-grid-cols-12 odin-gap-6"
          fieldsConfig={fieldsConfig}
        />
      </div>
      <WorkersFoundModal
        isOpen={!!existingWorkers.length}
        workers={existingWorkers}
        setWorkerMatch={setWorkerMatch}
        toggle={(): void => {
          setExistingWorkers([]);
        }}
        workerMatch={workerMatch}
        onCancel={(): void => {
          if (basicInfo) {
            createContractorWorker({
              variables: getCreateContractorWorkerVariables(basicInfo, true),
            });
          }
          setExistingWorkers([]);
        }}
        onAction={(): void => {
          history.push(`/worker/${workerMatch?.workerId}`);
        }}
      />
    </Container>
  );
}
